import {
    FrequencyUnits,
    IHierarchyChild,
    ISessionCreateRequest,
    ITimeRangeResponse,
    ITreeItemResponse,
    MetricTypeCodes,
    SessionSelectedPointChild,
    SessionTimeRangeChild,
} from '@kcftech/prometheus-client';
import {
    clearUserValueFromStorage,
    loadUserValueFromStorage,
    saveUserValueToStorage,
} from 'custom_hooks/useStorage';
import { subSeconds } from 'date-fns';
import { DefaultWorkbenchStateValues } from 'pages/workbench/state';
import {
    ActivityFilters,
    BurstDataType,
    FrequencyScale,
    IndicatorFilterTabType,
    IndicatorInfo,
    IndicatorSelectionMode,
    MarkerModes,
    SelectedSeriesPoint,
    SidebandCount,
    TimeMarkerMeta,
    WaterfallSelectedPoint,
} from 'pages/workbench/types';
import {
    WaterfallChartMaxSeriesOffset,
    WaterfallChartMinSeriesOffset,
} from 'shared/chart/core/frequency_waterfall/shared';
import { ChartRange } from 'shared/chart/core/types';
import { TrendLineColorsList } from 'utils/colors';
import { getPartialObject, getPropertyNames } from 'utils/object';
import {
    isArray,
    isBoolean,
    isNumber,
    isNumberInRangeInclusive,
    isObjectValid,
    isPartialObjectValid,
    isString,
    ObjectConstraints,
    valueInList,
} from 'utils/object-validation';
import { WorkbenchTab } from 'utils/RouteHelper';
import { CustomTimeRange, DateRange, SystemTimeRange, TimeRange } from 'utils/types';

import {
    StoredWorkbenchStateValues,
    WorkbenchState,
    WorkbenchStateValues,
} from './state/types';
import { DataPlatformApi } from '@kcftech/data-platform-sdk';

const WorkbenchStorageKey = 'workbench.state.storage';

export const TabsToShowIndicatorSelection: ReadonlyArray<WorkbenchTab> = [
    WorkbenchTab.Trend,
];

export const TabsToShowFilterSelection: ReadonlyArray<WorkbenchTab> = [
    WorkbenchTab.Indicators,
    WorkbenchTab.Trend,
];

export const getWorkbenchPageTitle = (
    groups: ITreeItemResponse[] | undefined,
    tabLabel?: string,
) => {
    if (!groups) {
        return tabLabel;
    }
    if (groups.length === 1) {
        return `${groups[0].Name} ${tabLabel}`;
    }
    if (groups.length >= 1) {
        return `Multiple ${tabLabel}`;
    }
    return tabLabel;
};

/**
 * Checks to see if the time range is in the list of valid corporation time ranges or if the time range is custom
 * @param timeRange The time range
 * @param corporationTimeRanges Valid corporation time ranges
 * @returns if the time range is valid
 */
export const isWorkbenchTimeRangeValid = (
    timeRange: TimeRange,
    corporationTimeRanges: ITimeRangeResponse[],
) =>
    timeRange.type === 'custom' ||
    corporationTimeRanges.some(({ Id }) => Id === timeRange.id);

export const convertChartRangeToDateRange = ({ min, max }: ChartRange): DateRange => ({
    startDate: new Date(min),
    endDate: new Date(max),
});

export const getChartRangeFromTimeRange = (timeRange: TimeRange): ChartRange => {
    if (timeRange.type === 'system') {
        const now = new Date();

        return {
            min: subSeconds(now, timeRange.timeInSeconds).getTime(),
            max: now.getTime(),
        };
    }

    return {
        min: timeRange.startTimestamp,
        max: timeRange.endTimestamp,
    };
};

export const getDateRangeFromTimeRange = (timeRange: TimeRange): DateRange => {
    const { min, max } = getChartRangeFromTimeRange(timeRange);

    return { startDate: new Date(min), endDate: new Date(max) };
};

export const getSessionCreateRequestData = (
    systemId: string,
    {
        customIndicatorFilterIds,
        indicatorTypes,
        sensorRoleTypes,
        indicatorFilterTabType,
        selectedGroupIds,
        selectedIndicators,
        selectedPoint,
        range,
        zoomRange,
        filtered,
        orientations,
        dataTypes,
        calculations,
        bands,
        labels,
        collectionFilter,
        showHistoricalIndicators,
    }: WorkbenchState,
): ISessionCreateRequest => {
    if (!range) {
        throw new Error('Missing range');
    }

    const startDate = new Date(range.min);
    const endDate = new Date(range.max);
    const startX = zoomRange ? new Date(zoomRange.min) : startDate;
    const endX = zoomRange ? new Date(zoomRange.max) : endDate;

    const data: ISessionCreateRequest = {
        SystemId: systemId,
        CustomFilterIds: customIndicatorFilterIds,
        IndicatorTypes: indicatorTypes
            .filter((type) => type !== 'ultrasound')
            .map((type) => type),
        UltrasoundQuickFilterSelected: indicatorTypes.includes('ultrasound'),
        MeasureOfs: sensorRoleTypes.map((type) => type ?? undefined),
        GroupIds: selectedGroupIds,
        SelectedIndicatorIds: selectedIndicators.map(({ indicatorId }) => indicatorId),
        SelectedPoint: selectedPoint
            ? new SessionSelectedPointChild({
                  XValue: selectedPoint.xValue,
                  SeriesId: selectedPoint.seriesId,
              })
            : undefined,
        TimeRange: new SessionTimeRangeChild({
            StartDate: startDate,
            EndDate: endDate,
            StartX: startX,
            EndX: endX,
        }),
        IsCustomFilterType: indicatorFilterTabType === 'custom',
        Filtered: filtered,
        // @ts-expect-error TS is complaining about the possibility of undefined values in the array, but the SDK is incorrect.
        Orientations: orientations.map((orientation) => orientation ?? undefined),
        DataTypes: dataTypes,
        Calculations: calculations,
        // @ts-expect-error TS is complaining about the possibility of undefined values in the array, but the SDK is incorrect.
        Bands: bands.map((band) => band ?? undefined),
        Labels: labels,
        CollectionFilter: collectionFilter,
        ShowHistoricalIndicators: showHistoricalIndicators,
    };

    return data;
};

export const findInHierarchy = (
    treeItem: ITreeItemResponse,
    where: (item: IHierarchyChild) => boolean,
): IHierarchyChild | undefined => [...treeItem.Hierarchy].reverse().find(where);

export const findMonitoringPointId = (treeItem: ITreeItemResponse) =>
    treeItem.TreeItemType === 'MonitoringPoint'
        ? treeItem.Id
        : findInHierarchy(treeItem, (item) => item.HierarchyType === 'MonitoringPoint')
              ?.HierarchyId;

const ValidBurstDataType: BurstDataType[] = ['velocity', 'acceleration'];

const ValidSidebandCount: SidebandCount[] = [1, 2, 3, 4, 5];

const ValidIndicatorFilterTabType: IndicatorFilterTabType[] = [
    'custom',
    'type',
    'advanced',
];

const ValidIndicatorSelectionMode: IndicatorSelectionMode[] = ['manual', 'auto'];

const ValidFrequencyUnits: FrequencyUnits[] = Object.values(FrequencyUnits);
const ValidFrequencyScaleUnits: FrequencyScale[] = ['linear', 'log'];

const SystemTimeRangeConstraints: ObjectConstraints<SystemTimeRange> = {
    type: (value) => value === 'system',
    id: isString,
    timeInSeconds: isNumber,
};

const CustomTimeRangeConstraints: ObjectConstraints<CustomTimeRange> = {
    type: (value) => value === 'custom',
    id: isString,
    startTimestamp: isNumber,
    endTimestamp: isNumber,
};

const isValidTimeRange = (value: unknown): value is TimeRange =>
    isObjectValid(value, SystemTimeRangeConstraints) ||
    isObjectValid(value, CustomTimeRangeConstraints);

const ChartRangeConstraints: ObjectConstraints<ChartRange> = {
    min: isNumber,
    max: isNumber,
};

const isValidChartRange = (value: unknown): value is ChartRange =>
    isObjectValid(value, ChartRangeConstraints);

const SelectedSeriesPointConstraints: ObjectConstraints<SelectedSeriesPoint> = {
    xValue: isNumber,
    seriesId: isString,
};

const isValidSelectedSeriesPoint = (value: unknown): value is SelectedSeriesPoint =>
    isObjectValid(value, SelectedSeriesPointConstraints);

const WaterfallSelectedPointConstraints: ObjectConstraints<WaterfallSelectedPoint> = {
    ...SelectedSeriesPointConstraints,
    indicatorId: isString,
};

const isValidWaterfallSelectedPoint = (value: unknown): value is WaterfallSelectedPoint =>
    isObjectValid(value, WaterfallSelectedPointConstraints);

const ActivityFiltersConstraints: ObjectConstraints<ActivityFilters> = {
    categoryFilterIds: (value) => isArray(value, isNumber),
    alertFilterIds: (value) => isArray(value, isNumber),
    automaticFilterIds: (value) => isArray(value, isNumber),
    machineIQFilterIds: (value) => isArray(value, isNumber),
};

const isValidActivityFilters = (value: unknown): value is ActivityFilters =>
    isObjectValid(value, ActivityFiltersConstraints);

const IndicatorInfoConstraints: ObjectConstraints<IndicatorInfo> = {
    indicatorId: isString,
    color: (value) => valueInList(value, TrendLineColorsList),
};

const isValidIndicatorInfo = (value: unknown): value is IndicatorInfo =>
    isObjectValid(value, IndicatorInfoConstraints);

const TimeMarkerMetaConstraints: ObjectConstraints<TimeMarkerMeta> = {
    id: isString,
    zeroCycleMs: isNumber,
    visible: isBoolean,
};

const isValidTimeMarkerMeta = (value: unknown): value is TimeMarkerMeta =>
    isObjectValid(value, TimeMarkerMetaConstraints);

const isChartRangeOrUndefined = (value: unknown) =>
    value === undefined || isValidChartRange(value);

const isValidBurstDataType = (value: unknown) => valueInList(value, ValidBurstDataType);

const isValidFrequencyScale = (value: unknown) =>
    valueInList(value, ValidFrequencyScaleUnits);

const indicatorTypes = new Set(Object.values(MetricTypeCodes) as string[]);
const isIndicatorType = (v: unknown) =>
    isString(v) && (indicatorTypes.has(v) || v === 'ultrasound');

const WorkbenchStateValueConstraints: ObjectConstraints<WorkbenchStateValues> = {
    isInitialized: isBoolean,
    runningSpeedHz: isNumber,
    runningSpeedUnit: (value) => valueInList(value, ValidFrequencyUnits),
    runningSpeedLocked: isBoolean,
    frequencyBurstDataType: isValidBurstDataType,
    timeBurstDataType: isValidBurstDataType,
    sidebandFrequencyHz: isNumber,
    sidebandCount: (value) => valueInList(value, ValidSidebandCount),
    bearingId: (value) => value === undefined || isString(value),
    rulerMarkerFrequencyHz: isNumber,
    selectedMarkerIds: (value) => isArray(value, isString),
    sidebandMarkerId: (value) => value === undefined || isString(value),
    harmonicMarkerIds: (value) => isArray(value, isString),
    timeMarkerMeta: (value) => isArray(value, isValidTimeMarkerMeta),
    showActivityLine: isBoolean,
    isTimeSeriesChartExpanded: isBoolean,
    showFrequencySpectrumChart: isBoolean,
    isFrequencySpectrumChartExpanded: isBoolean,
    showTimeDomainChart: isBoolean,
    isTimeDomainChartExpanded: isBoolean,
    showFrequencyMarkerTable: isBoolean,
    showTimeMarkerTable: isBoolean,
    showWaterfallValuesTable: isBoolean,
    selectedPoint: (value) => value === undefined || isValidSelectedSeriesPoint(value),
    timeDomainSelectedPoint: (value) =>
        value === undefined || isValidSelectedSeriesPoint(value),
    frequencyDomainSelectedPoint: (value) =>
        value === undefined || isValidSelectedSeriesPoint(value),
    waterfallSelectedPoint: (value) =>
        value === undefined || isValidWaterfallSelectedPoint(value),
    showMinMax: isBoolean,
    showThresholds: isBoolean,
    autoScroll: isBoolean,
    splineSmoothing: isBoolean,
    showBaseline: isBoolean,
    timeRange: (value) => value === undefined || isValidTimeRange(value),
    range: (value) => isValidChartRange(value),
    zoomRange: isChartRangeOrUndefined,
    frequencyChartZoomRange: isChartRangeOrUndefined,
    fixedFrequencyChartYAxisRange: isChartRangeOrUndefined,
    frequencyChartYAxisZoom: isChartRangeOrUndefined,
    frequencyChartScale: isValidFrequencyScale,
    timeChartZoomRange: isChartRangeOrUndefined,
    fixedTimeChartYAxisRange: isChartRangeOrUndefined,
    timeChartYAxisZoom: isChartRangeOrUndefined,
    fixedTimeSeriesLeftYAxisRange: isChartRangeOrUndefined,
    timeSeriesLeftYAxisZoom: isChartRangeOrUndefined,
    fixedTimeSeriesRightYAxisRange: isChartRangeOrUndefined,
    timeSeriesRightYAxisZoom: isChartRangeOrUndefined,
    activityFilters: isValidActivityFilters,
    customIndicatorFilterIds: (value) => isArray(value, isString),
    indicatorTypes: (value) => isArray(value, isIndicatorType),
    sensorRoleTypes: (value) => isArray(value, isString),
    dataTypes: (value) => isArray(value, isString),
    calculations: (value) => isArray(value, isString),
    bands: (value) => isArray(value, isString),
    labels: (value) => isArray(value, isString),
    filtered: (value) => value === undefined || isBoolean(value),
    collectionFilter: (value) =>
        isArray(value, isBoolean) && new Set(value).size === value.length,
    orientations: (value) =>
        isArray(value, (v) => valueInList(v, Object.values(DataPlatformApi.Orientation))),
    showHistoricalIndicators: isBoolean,
    indicatorFilterTabType: (value) => valueInList(value, ValidIndicatorFilterTabType),
    selectedGroupIds: (value) => isArray(value, isString),
    selectedIndicators: (value) => isArray(value, isValidIndicatorInfo),
    indicatorSelectionMode: (value) => valueInList(value, ValidIndicatorSelectionMode),
    isTreeVisible: isBoolean,
    expandedGroupIds: (value) => isArray(value, isString),
    showTreeStatusIcons: isBoolean,
    showTreeOfflineIcons: isBoolean,
    showTreeIssueIcons: isBoolean,
    isLoadingIndicators: isBoolean,
    fixedWaterfallChartYAxisRange: isChartRangeOrUndefined,
    isWaterfallChartExpanded: isBoolean,
    waterfallBurstDataType: isValidBurstDataType,
    waterfallChartScale: isValidFrequencyScale,
    waterfallChartSeriesOffset: (value) =>
        value === undefined ||
        isNumberInRangeInclusive(
            value,
            WaterfallChartMinSeriesOffset,
            WaterfallChartMaxSeriesOffset,
        ),
    waterfallChartYAxisZoom: isChartRangeOrUndefined,
    waterfallChartZoomRange: isChartRangeOrUndefined,
    waterfallFrequencyPoints: (value) => isArray(value, isValidSelectedSeriesPoint),
    waterfallSplineSmoothing: isBoolean,
    isAddingHealthRange: isBoolean,
    selectedHealthRangeIds: (value) => isArray(value, isString),
    editingHealthRangeId: isString,
    markerMode: (value) => (MarkerModes as readonly unknown[]).includes(value),
    pendingAcknowledgeAsset: (value) => value === undefined,
    downsamplingMethod: (value) =>
        value === undefined ||
        valueInList(value, Object.values(DataPlatformApi.DownsamplingMethod)),
};

export const getWorkbenchStateValues = (state: WorkbenchState): WorkbenchStateValues =>
    getPartialObject(state, getPropertyNames(WorkbenchStateValueConstraints));

const {
    isInitialized: _isInitialized,
    pendingAcknowledgeAsset: _pendingAcknowledgeAsset,
    ...PersistedWorkbenchStateValueConstraints
} = WorkbenchStateValueConstraints;

const StoredWorkbenchStateValueConstraints: ObjectConstraints<StoredWorkbenchStateValues> =
    {
        ...PersistedWorkbenchStateValueConstraints,
        systemId: (value) => value === undefined || isString(value),
    };

export const isValidWorkbenchState = (
    state: unknown,
): state is Partial<StoredWorkbenchStateValues> =>
    isPartialObjectValid(state, StoredWorkbenchStateValueConstraints);

const parseWorkbenchState = (json: unknown): StoredWorkbenchStateValues | null => {
    if (typeof json !== 'string') {
        return null;
    }
    try {
        const parsedState = JSON.parse(json);

        return isValidWorkbenchState(parsedState)
            ? { ...DefaultWorkbenchStateValues, ...parsedState }
            : null;
    } catch {
        return null;
    }
};

export const loadWorkbenchStateFromBrowserStorage = (userEmail: string) =>
    loadUserValueFromStorage<StoredWorkbenchStateValues | null, unknown>(
        userEmail,
        WorkbenchStorageKey,
        null,
        {
            store: 'session',
            deserialize: parseWorkbenchState,
        },
    ) ??
    loadUserValueFromStorage<StoredWorkbenchStateValues | null, unknown>(
        userEmail,
        WorkbenchStorageKey,
        null,
        { store: 'local', deserialize: parseWorkbenchState },
    ) ??
    undefined;

export const saveWorkbenchStateToBrowserStorage = (
    userEmail: string,
    state: StoredWorkbenchStateValues,
) => {
    const stateValues = getPartialObject(
        state,
        getPropertyNames(StoredWorkbenchStateValueConstraints),
    );
    const json = JSON.stringify(stateValues, null);

    saveUserValueToStorage(userEmail, WorkbenchStorageKey, json, { store: 'session' });
    saveUserValueToStorage(userEmail, WorkbenchStorageKey, json, { store: 'local' });
};

export const clearWorkbenchStateBrowserStorage = (userEmail: string) => {
    clearUserValueFromStorage(userEmail, WorkbenchStorageKey, 'session');
    clearUserValueFromStorage(userEmail, WorkbenchStorageKey, 'local');
};

const isIndicatorSelected = (selectedIndicators: IndicatorInfo[], indicatorId: string) =>
    selectedIndicators.some((info) => info.indicatorId === indicatorId);

export const isSelectedPointValid = (
    selectedIndicators: IndicatorInfo[],
    selectedPoint: SelectedSeriesPoint | undefined,
) =>
    selectedPoint !== undefined &&
    isIndicatorSelected(selectedIndicators, selectedPoint.seriesId);

export const isWaterfallSelectedPointValid = (
    selectedIndicators: IndicatorInfo[],
    selectedPoint: WaterfallSelectedPoint | undefined,
) =>
    selectedPoint !== undefined &&
    isIndicatorSelected(selectedIndicators, selectedPoint.indicatorId);

export const convertFrequencyHzToPeriodMs = (frequencyHz: number) =>
    frequencyHz ? 1000 / frequencyHz : 0;

export const convertPeriodMsToFrequencyHz = (periodMs: number) =>
    periodMs ? 1000 / periodMs : 0;

export const clearURLQueryParams = () => {
    const url = new URL(window.location.href);

    Array.from(url.searchParams.keys()).forEach((param) =>
        url.searchParams.delete(param),
    );

    window.history.replaceState({}, '', url.toString());
};

export const maxHarmonicMarkers = 3;
export const maxTimeMarkers = 3;
